import {
  type PreviewKind,
  getPlacementByPreviewKind,
} from '@/lib/platform-matrix/placement-matrix';
import { cn } from '@/lib/utils';

interface SafeZoneOverlayProps {
  /** Drives the safe-zone bands + confidence from the canonical matrix. */
  previewKind: PreviewKind;
  className?: string;
}

// Treatment B + competitive-UX contrast refinement. Pixel values are hardcoded
// because they sit ON TOP of media (like image content) and must NOT invert
// with the app theme. Exact opacities are tunable during visual QA.
const SCRIM = 'rgba(17, 17, 17, 0.22)';
const LINE = 'rgba(255, 255, 255, 0.92)';
// Dark halo so the white bleed line stays visible on bright/white media
// (the "line vanishes on a snow shot" review finding).
const LINE_HALO = '0 0 0 0.5px rgba(0, 0, 0, 0.55)';

/** Frame fraction (0..1) → CSS percent, rounded to kill float artifacts. */
function pct(v: number): string {
  return `${+(v * 100).toFixed(2)}%`;
}

/**
 * Platform safe-zone overlay — dims the regions a platform's UI chrome covers
 * (caption, buttons, profile) so a creator can see what gets hidden BEFORE
 * posting. Keyed off `previewKind` (NOT platform id) so a feed post never shows
 * Reels chrome. Renders nothing when the placement has no chrome. Non-`verified`
 * safe-zone data (everything but TikTok today) is labelled "approx."
 */
export function SafeZoneOverlay({ previewKind, className }: SafeZoneOverlayProps) {
  const row = getPlacementByPreviewKind(previewKind);
  if (!row || !row.hasChrome) return null;

  const { top, bottom, side } = row.safeZones;
  const isApprox = row.safeZoneConfidence !== 'verified';
  const sideInset = side > 0 ? pct(side) : '0';
  const bleedLineStyle = { borderTop: `1px solid ${LINE}`, boxShadow: LINE_HALO } as const;

  return (
    <div
      className={cn('pointer-events-none absolute inset-0', className)}
      data-approx={isApprox}
      aria-hidden
    >
      {top > 0 ? (
        <div
          data-zone="top"
          className="absolute left-0 right-0 top-0"
          style={{ height: pct(top), background: SCRIM }}
        />
      ) : null}
      {bottom > 0 ? (
        <div
          data-zone="bottom"
          className="absolute bottom-0 left-0 right-0"
          style={{ height: pct(bottom), background: SCRIM }}
        />
      ) : null}
      {side > 0 ? (
        <>
          <div
            data-zone="left"
            className="absolute bottom-0 left-0 top-0"
            style={{ width: pct(side), background: SCRIM }}
          />
          <div
            data-zone="right"
            className="absolute bottom-0 right-0 top-0"
            style={{ width: pct(side), background: SCRIM }}
          />
        </>
      ) : null}

      {top > 0 ? (
        <div
          data-bleed="top"
          className="absolute"
          style={{ top: pct(top), left: sideInset, right: sideInset, ...bleedLineStyle }}
        />
      ) : null}
      {bottom > 0 ? (
        <div
          data-bleed="bottom"
          className="absolute"
          style={{ bottom: pct(bottom), left: sideInset, right: sideInset, ...bleedLineStyle }}
        />
      ) : null}

      {bottom > 0 ? (
        <span
          className="absolute rounded px-1 py-0.5 text-micro font-medium"
          style={{
            bottom: `calc(${pct(bottom)} + 4px)`,
            right: sideInset === '0' ? '8px' : sideInset,
            color: 'rgba(255, 255, 255, 0.95)',
            // Stronger backplate so the label reads on dark media too (it
            // blended into a dark photo at 0.4). Verified in visual QA.
            background: 'rgba(0, 0, 0, 0.62)',
          }}
        >
          {isApprox ? 'safe area · approx.' : 'safe area'}
        </span>
      ) : null}
    </div>
  );
}
